import { Injectable } from '@angular/core';
import { UserListSdkService } from '@vendasta/contacts';
import { IAMService } from '@vendasta/iam';
import { LoadRequest, TableDataService } from '@vendasta/va-filter2-table';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { map, skipWhile, switchMap, take, tap } from 'rxjs/operators';
import { SortDirection } from '../../activation/core/interface';
import { UserList } from './user-list';
import { UserListsDetails } from './user-lists-details-table-model';

@Injectable({ providedIn: 'root' })
export class UserListsDetailsService implements TableDataService<UserListsDetails> {
  private totalResults$$: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  public readonly totalResults$: Observable<number> = this.totalResults$$.asObservable();
  private reload$$: Subject<boolean> = new Subject<boolean>();
  public readonly reload$: Observable<boolean> = this.reload$$.asObservable();
  private cursors: { [id: number]: string } = { 0: '' };
  private userList$$: BehaviorSubject<UserList> = new BehaviorSubject<UserList>(null);
  public readonly userList$: Observable<UserList> = this.userList$$.asObservable().pipe(skipWhile((n) => !n));

  constructor(
    private userListSdkService: UserListSdkService,
    private iamService: IAMService,
  ) {}

  load(request: LoadRequest): Observable<UserListsDetails[]> {
    return this.userList$.pipe(
      switchMap((userList) =>
        this.userListSdkService.listUsersFromList(userList.listId, this.cursors[request.pageIndex], request.pageSize),
      ),
      map((resp) => {
        if (resp.pagingMetadata.hasMore) {
          this.cursors[request.pageIndex + 1] = resp.pagingMetadata.nextCursor;
        }
        this.totalResults$$.next(resp.pagingMetadata.totalResults);
        if (!resp || !resp.userIds) {
          return [];
        }
        return resp.userIds;
      }),
      switchMap((userIds) => this.convertToUserListDetails(userIds)),
    );
  }

  convertToUserListDetails(userIds: string[]): Observable<UserListsDetails[]> {
    const userDetails: UserListsDetails[] = [];
    if (userIds.length === 0 || !userIds[0].startsWith('U-')) {
      return of(userDetails);
    }
    const userIdentifiers = userIds.map((u) => ({ userId: u }));
    return this.iamService.getMultiUsers(userIdentifiers).pipe(
      map((users) => {
        for (const u of users) {
          userDetails.push({
            userId: u.userId,
            userName: u.firstName + ' ' + u.lastName,
            userEmail: u.email,
          });
        }
        return userDetails.sort((child1, child2) => this.sortByName(child1, child2, 'ASCENDING'));
      }),
    );
  }

  sortByName(child1: UserListsDetails, child2: UserListsDetails, sortDirection: SortDirection): number {
    const child1Name = child1 && child1.userName ? child1.userName : '';
    const child2Name = child2 && child2.userName ? child2.userName : '';
    return sortDirection === 'ASCENDING' ? child1Name.localeCompare(child2Name) : child2Name.localeCompare(child1Name);
  }

  setUserList(listId: string): void {
    this.userListSdkService
      .getUserList(listId)
      .pipe(
        tap((resp) => {
          this.userList$$.next({
            listId: resp.listId,
            userListName: resp.listName,
            numUsers: resp.itemCount ? resp.itemCount : 0,
            lastModified: resp.updated,
          });
        }),
        take(1),
      )
      .subscribe();
  }

  deleteUser(userID: string): Observable<boolean> {
    return this.userListSdkService
      .deleteUserFromList(this.userList$$.value.listId, userID)
      .pipe(tap(() => this.reload$$.next(true)));
  }
}
